import React, { useContext, useMemo, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import {
  ElementTracking,
  TargetingDeviceType as TD,
  toTargetingDeviceType,
} from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import { capitalize } from '@/utils/types';
import { DeviceType as DT } from '@/utils/definitions';
import {
  StyledTooltip,
  TooltipRow,
} from '@/features/editor/widgets/changelog/shared/shared';

export function TrackEventTooltip({ elmTrack }: { elmTrack: ElementTracking }) {
  const {
    devicePreview: {
      editorState: { device },
    },
    experienceState,
  } = useContext(EditorContext);

  const tooltip = useRef(null);

  const isSameDeviceAsChange = useMemo(
    () => elmTrack?.targetDevice === toTargetingDeviceType(device),
    [device, elmTrack],
  );

  const oppositeDevice = useMemo<TD>(
    () => (device === DT.Desktop ? TD.MOBILE : TD.DESKTOP),
    [device],
  );
  const isTargetingAllDevices = useMemo<boolean>(
    () => elmTrack?.targetDevice === TD.ALL_DEVICES,
    [elmTrack],
  );

  const onDelete = async (ev) => {
    ev.stopPropagation();
    experienceState.deleteElementTracking({ ordinal: elmTrack.ordinal } as any);
  };

  const onDuplicate = async (ev) => {
    ev.stopPropagation();
    experienceState.duplicateElementTracking({
      ordinal: elmTrack.ordinal,
    } as any);
  };

  const onToggleTargetAllDevices = (ev: any) => {
    ev.stopPropagation();
    const targetDevice = isTargetingAllDevices
      ? toTargetingDeviceType(device)
      : TD.ALL_DEVICES;
    experienceState.setElementTracking({ ...elmTrack, targetDevice });

    hideTooltip();
  };

  const onCopyToDevice = (ev: any) => {
    ev.stopPropagation();

    experienceState.duplicateElementTracking({
      ordinal: elmTrack.ordinal,
      targetDevice: isSameDeviceAsChange ? oppositeDevice : device,
    } as any);
    hideTooltip();
  };

  const hideTooltip = () => {
    setTimeout(() => {
      tooltip.current.tooltipRef.style.display = `none`;
    }, 100);
    setTimeout(() => {
      tooltip.current.tooltipRef.style.display = `block`;
    }, 500);
  };

  return (
    <>
      <StyledTooltip />
      <ReactTooltip
        ref={tooltip}
        id={`track-${elmTrack.ordinal}`}
        place="bottom"
        effect="solid"
        clickable
        backgroundColor="#ffffff"
        textColor="#111111"
        arrowColor="#ffffff"
        className="exp-options-tooltip-cls"
        scrollHide
        delayHide={200}
        event="click"
        eventOff="mouseout"
      >
        <Wrapper>
          <TooltipRow onClick={onDuplicate}>Duplicate</TooltipRow>
          {!isTargetingAllDevices && (
            <TooltipRow onClick={onCopyToDevice}>
              Copy to{` `}
              {capitalize(isSameDeviceAsChange ? oppositeDevice : device)}
            </TooltipRow>
          )}
          <TooltipRow onClick={onToggleTargetAllDevices}>
            Target{` `}
            {isTargetingAllDevices ? capitalize(device) : `All Devices`}
          </TooltipRow>
          <TooltipRow onClick={onDelete}>Delete</TooltipRow>
        </Wrapper>
      </ReactTooltip>
    </>
  );
}

const Wrapper = styled.div`
  && > div:not(:last-child) {
    border-bottom: 1px solid #f1f1f1;
  }
`;
